import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

const ONBOARDING_STEPS = [
  {
    target: "[data-onboarding='welcome']",
    title: "Bienvenue sur Odys'IA !",
    content: "Je vais te guider pour decouvrir ta plateforme d'accompagnement immobilier.",
    position: "bottom",
  },
  {
    target: "[data-onboarding='agents']",
    title: "Tes 12 Experts IA",
    content: "Chaque expert est specialise dans un domaine : analyse, strategie, negociation, banque, fiscalite, et plus encore !",
    position: "top",
  },
  {
    target: "[data-onboarding='guide']",
    title: "Commence par ODYS'GUIDE",
    content: "Si tu ne sais pas par ou commencer, ODYS'GUIDE va t'orienter et creer ton plan d'action personnalise.",
    position: "bottom",
  },
  {
    target: "[data-onboarding='profile']",
    title: "Ton Profil",
    content: "Accede a tes parametres, change de theme, et gere tes notifications ici.",
    position: "bottom",
  },
];

export function OnboardingProvider({ children }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // L'onboarding ne vit QUE sur le dashboard : ses cibles n'existent que la, et
    // l'overlay plein ecran bloquerait les clics ailleurs (chat, login...).
    if (location.pathname !== "/dashboard") {
      setIsActive(false);
      return;
    }
    const hasSeenOnboarding = localStorage.getItem("odys-onboarding-complete");
    if (!hasSeenOnboarding) {
      const t = setTimeout(() => setIsActive(true), 1000);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isActive && ONBOARDING_STEPS[currentStep]) {
      const target = document.querySelector(ONBOARDING_STEPS[currentStep].target);
      setTargetElement(target);
      
      if (target) {
        // Add highlight class with high z-index
        target.style.position = 'relative';
        target.style.zIndex = '50';
        target.classList.add("onboarding-highlight");
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      
      return () => {
        if (target) {
          target.style.position = '';
          target.style.zIndex = '';
          target.classList.remove("onboarding-highlight");
        }
      };
    }
  }, [currentStep, isActive]);

  const nextStep = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const completeOnboarding = () => {
    localStorage.setItem("odys-onboarding-complete", "true");
    setIsActive(false);
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  const resetOnboarding = () => {
    localStorage.removeItem("odys-onboarding-complete");
    setCurrentStep(0);
    setIsActive(true);
  };

  return (
    <>
      {children}
      
      {isActive && (
        <>
          {/* Overlay with cutout for highlighted element */}
          <div 
            className="fixed inset-0 z-40 pointer-events-none"
            style={{
              background: 'rgba(0, 0, 0, 0.6)',
            }}
          />
          
          {/* Clickable overlay to skip */}
          <div 
            className="fixed inset-0 z-40"
            onClick={skipOnboarding}
            data-testid="onboarding-overlay"
            style={{ background: 'transparent' }}
          />
          
          {/* Tooltip */}
          {targetElement && (
            <OnboardingTooltip
              step={ONBOARDING_STEPS[currentStep]}
              currentStep={currentStep}
              totalSteps={ONBOARDING_STEPS.length}
              targetElement={targetElement}
              onNext={nextStep}
              onPrev={prevStep}
              onSkip={skipOnboarding}
            />
          )}
        </>
      )}
    </>
  );
}

function OnboardingTooltip({ step, currentStep, totalSteps, targetElement, onNext, onPrev, onSkip }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const tooltipWidth = 320;
      
      let top, left;
      
      if (step.position === "bottom") {
        top = rect.bottom + 16;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      } else {
        top = rect.top - 150;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      }
      
      // Keep within viewport
      left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
      
      setPosition({ top, left });
    }
  }, [targetElement, step]);

  return (
    <div 
      className={`onboarding-tooltip ${step.position}`}
      style={{ 
        top: position.top, 
        left: position.left,
        width: 320,
      }}
    >
      <button 
        onClick={onSkip}
        data-testid="onboarding-close-btn"
        className="absolute top-2 right-2 text-primary-foreground/70 hover:text-primary-foreground"
      >
        <X className="w-4 h-4" />
      </button>
      
      <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
      <p className="text-sm opacity-90 mb-4">{step.content}</p>
      
      <div className="flex items-center justify-between">
        <span className="text-xs opacity-70">
          {currentStep + 1} / {totalSteps}
        </span>
        
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={onPrev}
              data-testid="onboarding-prev-btn"
              className="text-primary-foreground hover:bg-primary-foreground/20"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          <Button 
            size="sm" 
            onClick={onNext}
            data-testid="onboarding-next-btn"
            className="bg-white text-primary hover:bg-white/90"
          >
            {currentStep === totalSteps - 1 ? "Terminer" : "Suivant"}
            {currentStep < totalSteps - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function useOnboarding() {
  const resetOnboarding = () => {
    localStorage.removeItem("odys-onboarding-complete");
    window.location.reload();
  };
  
  return { resetOnboarding };
}
